/** Shared helpers for stacked chart filters / dimensions. */

export const isFilterVisible = (visibility = {}, key) => visibility[key] !== false;

export const resolveDimensionValue = (item, dimension) => {
  const raw = item?.[dimension.key];
  if (typeof dimension.normalize === 'function') {
    return dimension.normalize(raw) || dimension.fallback || 'Sin dato';
  }
  const trimmed = String(raw || '').trim();
  return trimmed || dimension.fallback || 'Sin dato';
};

export const discoverDimensionOptions = (items = [], dimension) => {
  const discovered = new Set();
  items.forEach((item) => {
    discovered.add(resolveDimensionValue(item, dimension));
  });
  const preferred = Array.isArray(dimension.order) ? dimension.order : [];
  return [
    ...preferred.filter((key) => discovered.has(key)),
    ...[...discovered].filter((key) => !preferred.includes(key)).sort((a, b) => a.localeCompare(b, 'es')),
  ].map((key) => ({
    key,
    label: key,
    color: dimension.colors?.[key],
  }));
};

export const filterItemsByDimensions = (items = [], dimensions = [], visibilityByDimension = {}) =>
  items.filter((item) =>
    dimensions.every((dimension) => {
      const value = resolveDimensionValue(item, dimension);
      return isFilterVisible(visibilityByDimension[dimension.key], value);
    })
  );

export const toggleVisibilityKey = (prev, key) => ({
  ...prev,
  [key]: !isFilterVisible(prev, key),
});

export const visibilityMapFromOptions = (options = [], visible) =>
  options.reduce((acc, option) => {
    acc[option.key] = visible;
    return acc;
  }, {});

/** Default chart metrics: count vs monetary amount. */
export const CHART_METRIC_COUNT = { key: 'count', label: 'Cantidad', totalLabel: 'cotizaciones' };
export const CHART_METRIC_ITEMS = {
  key: 'items',
  label: 'Ítems cotizados',
  totalLabel: 'ítems',
};
export const CHART_METRIC_AMOUNT = {
  key: 'amount',
  label: 'Montos',
  totalLabel: 'c/IVA (CLP)',
};
export const CHART_METRIC_GALLONS = {
  key: 'gallons',
  label: 'Galones T-COAT',
  totalLabel: 'galones T-COAT',
};
export const CHART_METRIC_SALE_RATE = {
  key: 'saleRate',
  label: 'Tasa de venta',
  totalLabel: '',
};
export const CHART_METRIC_ADJUSTED_SALE_RATE = {
  key: 'adjustedSaleRate',
  label: 'Tasa de venta ajustada',
  totalLabel: '',
};

export const DEFAULT_CHART_VALUE_METRICS = [
  CHART_METRIC_COUNT,
  CHART_METRIC_ITEMS,
  CHART_METRIC_AMOUNT,
  CHART_METRIC_GALLONS,
];

export const isChartRateMetric = (metricKey = '') =>
  metricKey === 'saleRate' || metricKey === 'adjustedSaleRate';

/** statusByQuoteId: Map<quoteId, status> */
export const computeSaleRateFromStatuses = (statusByQuoteId, adjusted = false) => {
  let sold = 0;
  let lost = 0;
  let total = 0;
  (statusByQuoteId || new Map()).forEach((status) => {
    total += 1;
    const normalized = String(status || '').trim();
    if (normalized === 'Vendida') sold += 1;
    else if (normalized === 'Perdida') lost += 1;
  });
  if (adjusted) {
    const denom = sold + lost;
    return denom > 0 ? (sold / denom) * 100 : 0;
  }
  return total > 0 ? (sold / total) * 100 : 0;
};

/**
 * Rate bars: one series per bucket (period/region). Stacking percentages is not meaningful.
 * buckets: [{ key, label, shortLabel?, items: [] }]
 */
export const aggregateSaleRateRows = ({
  buckets = [],
  metricKey = 'saleRate',
  seriesLabel = 'Tasa',
}) => {
  const adjusted = metricKey === 'adjustedSaleRate';
  const rows = (buckets || []).map((bucket) => {
    const statusById = new Map();
    (bucket.items || []).forEach((item, index) => {
      const uid = getChartUniqueKey(item) || getChartUniqueKey(item?.sample) || `line-${bucket.key}-${index}`;
      if (statusById.has(uid)) return;
      const status =
        String(item?.status || item?.sample?.status || '').trim() || 'Sin estado';
      statusById.set(uid, status);
    });
    const rate = computeSaleRateFromStatuses(statusById, adjusted);
    return {
      key: bucket.key,
      label: bucket.label,
      shortLabel: bucket.shortLabel,
      total: rate,
      bySeries: { [seriesLabel]: rate },
    };
  });
  return { rows, seriesPresent: [seriesLabel] };
};

const LITERS_PER_GALLON = 3.785411784;

/** Convierte un formato de envase (Galón, Tineta 16 L, etc.) a galones equivalentes. */
export const unitToEquivalentGallons = (unit = '') => {
  const raw = String(unit || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (!raw) return 0;

  if (/(1\s*\/\s*4|0[.,]25)\s*gal/.test(raw) || raw.includes('1/4 gal')) return 0.25;
  if (/^(galon|galones)$/.test(raw) || /^galon\b/.test(raw)) return 1;

  // Equivalencia comercial T-COAT: Tineta 16 L = 4 gal, Tineta 20 L = 5 gal.
  if (/tineta\s*16\s*l/.test(raw)) return 4;
  if (/tineta\s*20\s*l/.test(raw)) return 5;

  const gallonsMatch = raw.match(/^(\d+(?:[.,]\d+)?)\s*gal/);
  if (gallonsMatch) return Number(gallonsMatch[1].replace(',', '.')) || 0;

  const literMatch = raw.match(/(\d+(?:[.,]\d+)?)\s*l(?:itros?)?\b/);
  if (literMatch) {
    const liters = Number(literMatch[1].replace(',', '.'));
    return Number.isFinite(liters) && liters > 0 ? liters / LITERS_PER_GALLON : 0;
  }

  return 0;
};

/**
 * T-COAT Diluyente Epóxico (BS005) en 1 L / 5 L no entra al total de galones T-COAT.
 * Otros formatos del mismo SKU (p. ej. Tineta) sí se consideran.
 */
export const isExcludedFromTcoatGallonTotal = (item = {}) => {
  const sku = String(item.product_sku || '')
    .trim()
    .toUpperCase();
  const name = String(item.item_name || item.product_name || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  const isDiluyenteEpoxico = sku === 'BS005' || /diluyente\s+epox/.test(name);
  if (!isDiluyenteEpoxico) return false;

  const unit = String(item.unit || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return /^(1|5)\s*l(?:itros?)?\b/.test(unit);
};

/** Galones equivalentes de una línea T-COAT (0 si el ítem está excluido del total). */
export const resolveTcoatLineGallons = (item = {}) => {
  if (isExcludedFromTcoatGallonTotal(item)) return 0;
  const qty = Number(item.quantity || 0);
  if (!Number.isFinite(qty) || qty <= 0) return 0;
  const gallons = qty * unitToEquivalentGallons(item.unit);
  return gallons > 0 ? gallons : 0;
};

/** Precio unitario neto ÷ galones equivalentes del envase. */
export const resolvePricePerGallon = (unitPrice, unit = '') => {
  const gallons = unitToEquivalentGallons(unit);
  const price = Number(unitPrice || 0);
  if (!(gallons > 0) || !Number.isFinite(price) || price <= 0) return 0;
  return price / gallons;
};

/** Unique key for counting cotizaciones/ventas (not line items). */
export const getChartUniqueKey = (item) => {
  const id =
    item?.purchaseOrderId ??
    item?.purchase_order_id ??
    item?.saleId ??
    item?.sale_id ??
    item?.quotationId ??
    item?.quotation_id ??
    item?.organizationId ??
    item?.organization_id ??
    item?.id ??
    null;
  if (id == null || id === '') return null;
  return String(id);
};

export const resolveItemWeight = (item, metricKey = 'count') => {
  if (metricKey === 'amount') {
    const amount = Number(item?.amount ?? item?.value ?? 0);
    return Number.isFinite(amount) && amount > 0 ? amount : 0;
  }
  if (metricKey === 'gallons') {
    const gallons = Number(item?.gallons ?? 0);
    return Number.isFinite(gallons) && gallons > 0 ? gallons : 0;
  }
  if (metricKey === 'items') {
    // Cotizaciones sin líneas no aportan ítems.
    if (item?.isEmptyQuote) return 0;
    return 1;
  }
  // count = unique quotations (handled in chart aggregation)
  return 1;
};

export const formatChartMetricValue = (value, metricKey = 'count') => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) {
    if (metricKey === 'amount') return '$0';
    if (isChartRateMetric(metricKey)) return '0%';
    return '0';
  }
  if (isChartRateMetric(metricKey)) {
    return `${Math.round(n)}%`;
  }
  if (metricKey === 'amount') {
    const abs = Math.abs(n);
    if (abs >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
    if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (abs >= 10_000) return `$${(n / 1_000).toFixed(0)}K`;
    return `$${Math.round(n).toLocaleString('es-CL')}`;
  }
  if (metricKey === 'gallons') {
    const abs = Math.abs(n);
    if (abs >= 10_000) return `${(n / 1_000).toFixed(1)}K`;
    if (abs >= 100) return Math.round(n).toLocaleString('es-CL');
    return (Math.round(n * 10) / 10).toLocaleString('es-CL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });
  }
  return Math.round(n).toLocaleString('es-CL');
};

export const formatChartMetricTooltip = (value, metricKey = 'count') => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) {
    if (metricKey === 'amount') return '$0';
    if (isChartRateMetric(metricKey)) return '0%';
    return '0';
  }
  if (isChartRateMetric(metricKey)) {
    return `${(Math.round(n * 10) / 10).toLocaleString('es-CL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    })}%`;
  }
  if (metricKey === 'amount') return `$${Math.round(n).toLocaleString('es-CL')}`;
  if (metricKey === 'gallons') {
    return `${(Math.round(n * 10) / 10).toLocaleString('es-CL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    })} gal`;
  }
  return Math.round(n).toLocaleString('es-CL');
};

export const chartSharePercent = (part, whole) => {
  const p = Number(part || 0);
  const w = Number(whole || 0);
  if (!Number.isFinite(p) || !Number.isFinite(w) || w <= 0) return 0;
  return (p / w) * 100;
};

export const formatChartSharePercent = (part, whole) => {
  const pct = chartSharePercent(part, whole);
  return `${pct.toLocaleString('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })}%`;
};

/** Absolute metric + share of a parent total (omit share for rate metrics). */
export const formatChartMetricWithShare = (value, parentTotal, metricKey = 'count') => {
  const absolute = formatChartMetricTooltip(value, metricKey);
  if (isChartRateMetric(metricKey)) return absolute;
  return `${absolute} (${formatChartSharePercent(value, parentTotal)})`;
};

/** Amount-range (histogram) helpers — values in millions of CLP. */
export const CLP_MILLION = 1_000_000;
export const MAX_AMOUNT_RANGE_BINS = 40;

export const DEFAULT_AMOUNT_RANGE_CONFIG = {
  minMillions: 0,
  maxMillions: 5,
  stepMillions: 1,
};

const roundMillions = (value) => Math.round(Number(value) * 1000) / 1000;

export const normalizeAmountRangeConfig = (config = {}) => {
  const parse = (value, fallback) => {
    if (value === '' || value == null) return fallback;
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };

  let minMillions = parse(config.minMillions, 0);
  let maxMillions = parse(config.maxMillions, 5);
  let stepMillions = parse(config.stepMillions, 1);

  if (minMillions < 0) minMillions = 0;
  if (maxMillions <= minMillions) maxMillions = minMillions + 1;
  if (stepMillions <= 0) stepMillions = 1;

  const span = maxMillions - minMillions;
  if (span / stepMillions > MAX_AMOUNT_RANGE_BINS) {
    stepMillions = roundMillions(span / MAX_AMOUNT_RANGE_BINS) || stepMillions;
  }

  return {
    minMillions: roundMillions(minMillions),
    maxMillions: roundMillions(maxMillions),
    stepMillions: roundMillions(stepMillions),
  };
};

export const formatMillionsLabel = (millions) => {
  const n = roundMillions(millions);
  if (n === 0) return '$0';
  const text = Number.isInteger(n) ? String(n) : String(n);
  return `$${text}M`;
};

/** Build histogram bins from [min, max] with step; includes <min and ≥max overflow buckets. */
export const buildAmountRangesFromConfig = (config = DEFAULT_AMOUNT_RANGE_CONFIG) => {
  const { minMillions, maxMillions, stepMillions } = normalizeAmountRangeConfig(config);
  const ranges = [];

  if (minMillions > 0) {
    ranges.push({
      key: `lt-${minMillions}`,
      min: 0,
      max: minMillions * CLP_MILLION,
      label: `< ${formatMillionsLabel(minMillions)}`,
      shortLabel: `<${minMillions}M`,
      isOverflow: true,
    });
  }

  for (let start = minMillions; start < maxMillions - 1e-9; start += stepMillions) {
    const startR = roundMillions(start);
    const endR = roundMillions(Math.min(start + stepMillions, maxMillions));
    if (endR <= startR) break;
    ranges.push({
      key: `${startR}-${endR}`,
      min: startR * CLP_MILLION,
      max: endR * CLP_MILLION,
      label: `${formatMillionsLabel(startR)}–${formatMillionsLabel(endR).replace(/^\$/, '')}`,
      shortLabel: `${startR}–${endR}M`,
      isOverflow: false,
    });
  }

  ranges.push({
    key: `gte-${maxMillions}`,
    min: maxMillions * CLP_MILLION,
    max: Infinity,
    label: `≥ ${formatMillionsLabel(maxMillions)}`,
    shortLabel: `≥${maxMillions}M`,
    isOverflow: true,
  });

  return ranges;
};

export const resolveAmountRange = (amount, ranges = []) => {
  const n = Number(amount || 0);
  const value = Number.isFinite(n) && n > 0 ? n : 0;
  for (let i = 0; i < ranges.length; i += 1) {
    const range = ranges[i];
    if (range.max === Infinity) {
      if (value >= range.min) return range;
      continue;
    }
    // Last closed bin before overflow: include exact max only via ≥ bucket.
    if (value >= range.min && value < range.max) return range;
  }
  return ranges[ranges.length - 1] || null;
};

/** Collapse line-level chart items into one row per quotation (for amount histograms). */
export const collapseItemsToQuotations = (items = [], getAmountValue) => {
  const byQuote = new Map();
  let lineIndex = 0;

  items.forEach((item) => {
    const uid = getChartUniqueKey(item) || `line-${lineIndex}`;
    lineIndex += 1;
    const hasQuotationAmount =
      item?.quotationAmount != null && Number.isFinite(Number(item.quotationAmount));
    const fallbackAmount =
      typeof getAmountValue === 'function'
        ? Number(getAmountValue(item) || 0)
        : Number(item?.amount || 0);

    if (!byQuote.has(uid)) {
      byQuote.set(uid, {
        quotationId: uid,
        amount: hasQuotationAmount
          ? Number(item.quotationAmount)
          : Number.isFinite(fallbackAmount)
            ? fallbackAmount
            : 0,
        gallons: 0,
        items: 0,
        sample: item,
        lockedAmount: hasQuotationAmount,
      });
    }

    const entry = byQuote.get(uid);
    if (hasQuotationAmount) {
      entry.amount = Number(item.quotationAmount);
      entry.lockedAmount = true;
    } else if (!entry.lockedAmount) {
      const lineAmount = Number(item?.amount || 0);
      if (Number.isFinite(lineAmount) && lineAmount > 0) entry.amount += lineAmount;
    }

    entry.gallons += Number(item?.gallons || 0) || 0;
    if (!item?.isEmptyQuote) entry.items += 1;
    entry.sample = item;
  });

  return [...byQuote.values()];
};

/** Aggregate quotation rows into amount-range buckets with optional stack series. */
export const aggregateByAmountRanges = ({
  quotationRows = [],
  ranges = [],
  activeDimension,
  metricKey = 'count',
}) => {
  const discovered = new Set();
  const countUnique = metricKey === 'count';
  const byRange = {};

  ranges.forEach((range) => {
    byRange[range.key] = countUnique
      ? { totalIds: new Set(), bySeriesIds: {} }
      : { total: 0, bySeries: {} };
  });

  quotationRows.forEach((row) => {
    const range = resolveAmountRange(row.amount, ranges);
    if (!range || !byRange[range.key]) return;
    const series = resolveDimensionValue(row.sample, activeDimension);

    if (countUnique) {
      byRange[range.key].totalIds.add(row.quotationId);
      if (!byRange[range.key].bySeriesIds[series]) byRange[range.key].bySeriesIds[series] = new Set();
      byRange[range.key].bySeriesIds[series].add(row.quotationId);
      discovered.add(series);
      return;
    }

    let weight = 0;
    if (metricKey === 'amount') weight = Number(row.amount || 0);
    else if (metricKey === 'gallons') weight = Number(row.gallons || 0);
    else if (metricKey === 'items') weight = Number(row.items || 0);
    else weight = resolveItemWeight(row.sample, metricKey);

    if (weight <= 0) return;
    byRange[range.key].total += weight;
    byRange[range.key].bySeries[series] = (byRange[range.key].bySeries[series] || 0) + weight;
    discovered.add(series);
  });

  const toStats = (entry) => {
    if (!entry) return { total: 0, bySeries: {} };
    if (!countUnique) return entry;
    const bySeries = {};
    Object.entries(entry.bySeriesIds || {}).forEach(([key, set]) => {
      bySeries[key] = set.size;
    });
    return { total: entry.totalIds.size, bySeries };
  };

  const preferredOrder = Array.isArray(activeDimension?.order) ? activeDimension.order : [];
  const seriesPresent = [
    ...preferredOrder.filter((key) => discovered.has(key)),
    ...[...discovered].filter((key) => !preferredOrder.includes(key)).sort((a, b) => a.localeCompare(b, 'es')),
  ];

  const rows = ranges.map((range) => {
    const stats = toStats(byRange[range.key]);
    return {
      key: range.key,
      label: range.label,
      shortLabel: range.shortLabel || range.label,
      total: stats.total,
      bySeries: stats.bySeries,
    };
  });

  return { rows, seriesPresent };
};

/** Gallon-price histogram helpers — config in miles de CLP (neto / galón). */
export const CLP_THOUSAND = 1_000;

export const DEFAULT_GALLON_PRICE_RANGE_CONFIG = {
  minThousands: 0,
  maxThousands: 25,
  stepThousands: 5,
};

const roundThousands = (value) => Math.round(Number(value) * 100) / 100;

export const normalizeGallonPriceRangeConfig = (config = {}) => {
  const parse = (value, fallback) => {
    if (value === '' || value == null) return fallback;
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };

  let minThousands = parse(config.minThousands, 0);
  let maxThousands = parse(config.maxThousands, 25);
  let stepThousands = parse(config.stepThousands, 5);

  if (minThousands < 0) minThousands = 0;
  if (maxThousands <= minThousands) maxThousands = minThousands + 5;
  if (stepThousands <= 0) stepThousands = 5;

  const span = maxThousands - minThousands;
  if (span / stepThousands > MAX_AMOUNT_RANGE_BINS) {
    stepThousands = roundThousands(span / MAX_AMOUNT_RANGE_BINS) || stepThousands;
  }

  return {
    minThousands: roundThousands(minThousands),
    maxThousands: roundThousands(maxThousands),
    stepThousands: roundThousands(stepThousands),
  };
};

export const formatThousandsLabel = (thousands) => {
  const n = roundThousands(thousands);
  if (n === 0) return '$0';
  const text = Number.isInteger(n) ? String(n) : String(n);
  return `$${text}K`;
};

export const buildGallonPriceRangesFromConfig = (config = DEFAULT_GALLON_PRICE_RANGE_CONFIG) => {
  const { minThousands, maxThousands, stepThousands } = normalizeGallonPriceRangeConfig(config);
  const ranges = [];

  if (minThousands > 0) {
    ranges.push({
      key: `lt-${minThousands}`,
      min: 0,
      max: minThousands * CLP_THOUSAND,
      label: `< ${formatThousandsLabel(minThousands)}`,
      shortLabel: `<${minThousands}K`,
      isOverflow: true,
    });
  }

  for (let start = minThousands; start < maxThousands - 1e-9; start += stepThousands) {
    const startR = roundThousands(start);
    const endR = roundThousands(Math.min(start + stepThousands, maxThousands));
    if (endR <= startR) break;
    ranges.push({
      key: `${startR}-${endR}`,
      min: startR * CLP_THOUSAND,
      max: endR * CLP_THOUSAND,
      label: `${formatThousandsLabel(startR)}–${formatThousandsLabel(endR).replace(/^\$/, '')}`,
      shortLabel: `${startR}–${endR}K`,
      isOverflow: false,
    });
  }

  ranges.push({
    key: `gte-${maxThousands}`,
    min: maxThousands * CLP_THOUSAND,
    max: Infinity,
    label: `≥ ${formatThousandsLabel(maxThousands)}`,
    shortLabel: `≥${maxThousands}K`,
    isOverflow: true,
  });

  return ranges;
};

/** Aggregate T-COAT line items into price-per-gallon buckets. */
export const aggregateByGallonPriceRanges = ({
  items = [],
  ranges = [],
  activeDimension,
  metricKey = 'count',
}) => {
  const discovered = new Set();
  const countUnique = metricKey === 'count';
  const byRange = {};

  ranges.forEach((range) => {
    byRange[range.key] = countUnique
      ? { totalIds: new Set(), bySeriesIds: {} }
      : { total: 0, bySeries: {} };
  });

  items.forEach((item, index) => {
    const price = Number(item?.pricePerGallon || 0);
    if (!(price > 0)) return;
    const range = resolveAmountRange(price, ranges);
    if (!range || !byRange[range.key]) return;
    const series = resolveDimensionValue(item, activeDimension);

    if (countUnique) {
      const uid = getChartUniqueKey(item) || `line-${index}`;
      byRange[range.key].totalIds.add(uid);
      if (!byRange[range.key].bySeriesIds[series]) byRange[range.key].bySeriesIds[series] = new Set();
      byRange[range.key].bySeriesIds[series].add(uid);
      discovered.add(series);
      return;
    }

    const weight = resolveItemWeight(item, metricKey);
    if (weight <= 0) return;
    byRange[range.key].total += weight;
    byRange[range.key].bySeries[series] = (byRange[range.key].bySeries[series] || 0) + weight;
    discovered.add(series);
  });

  const toStats = (entry) => {
    if (!entry) return { total: 0, bySeries: {} };
    if (!countUnique) return entry;
    const bySeries = {};
    Object.entries(entry.bySeriesIds || {}).forEach(([key, set]) => {
      bySeries[key] = set.size;
    });
    return { total: entry.totalIds.size, bySeries };
  };

  const preferredOrder = Array.isArray(activeDimension?.order) ? activeDimension.order : [];
  const seriesPresent = [
    ...preferredOrder.filter((key) => discovered.has(key)),
    ...[...discovered].filter((key) => !preferredOrder.includes(key)).sort((a, b) => a.localeCompare(b, 'es')),
  ];

  const rows = ranges.map((range) => {
    const stats = toStats(byRange[range.key]);
    return {
      key: range.key,
      label: range.label,
      shortLabel: range.shortLabel || range.label,
      total: stats.total,
      bySeries: stats.bySeries,
    };
  });

  return { rows, seriesPresent };
};
